import qs from 'qs';

// for some reason only the require worked..?
// import * as jwt_decode from 'jwt-decode';
const jwt_decode = require('jwt-decode');

import { navigateTo } from 'inside-out/components/shared/link';

import * as linkToken from './linkToken.graphql';
import * as logIn from './log-in.graphql';
import * as createToken from './createToken.graphql';
import * as createUserAndLogIn from './create-user-and-log-in.graphql';
import * as resetPassword from './reset-password.graphql';
import * as setNewPassword from './set-new-password.graphql';

export class AuthManager {
	// --- Singleton Stuff ---
	static instance;


	// Apollo Client Instance
	static initialise(client) {
		AuthManager.instance = new AuthManager(client);
	}


	static get sharedInstance() {
		if (!AuthManager.instance) {
			throw new Error('Instance is not yet available.');
		}
		return AuthManager.instance;
	}


	// --- Instance Stuff ---
	client = null;
	subscriptions = [];
	authenticatedRoutes = new Set();


	constructor(client) {
		this.client = client;

		// These aren't being created as bound arrow functions because of this bug:
		// https://github.com/babel/babel/issues/6806
		this.logIn = this.logIn.bind(this);
		this.createUserAndLogIn = this.createUserAndLogIn.bind(this);
		this.resetPassword = this.resetPassword.bind(this);
		this.saveQuestionnaireResults = this.saveQuestionnaireResults.bind(this);
	}


	subscribe = callback => {
		this.subscriptions.push(callback);
	};


	unsubscribe = callback => {
		this.subscriptions = this.subscriptions.filter(subscription => subscription !== callback);
	};


	enforceAuthenticated = () => {
		// Get the current URL and store it for later. When users log out they'll
		// get kicked back out to the login page.
		this.authenticatedRoutes.add(window.location.pathname);

		// And this should be called in componentDidMount, so we want to be sure
		// they're allowed to be here.
		this.forceLoginIfApplicable();
	};


	forceLoginIfApplicable = () => {
		// If we're currently on an authenticated route, force them over to the login page.
		if (!this.isLoggedIn && this.authenticatedRoutes.has(window.location.pathname)) {
			navigateTo(this.getLoginAndReturnLink());
		}
	};


	getLoginAndReturnLink = query => {
		if (typeof window === 'undefined') return '/log-in';

		const internalQuery = qs.parse(window.location.query, { ignoreQueryPrefix: true });
		Object.assign(internalQuery, query);

		const newQuery = {
			redirect:
				window.location.href.replace(window.location.origin, '') +
				qs.stringify(internalQuery, { addQueryPrefix: true }),
		};

		return '/log-in?' + qs.stringify(newQuery);
	};


	getRegisterAndReturnLink = query => {
		if (typeof window === 'undefined') return '/register';

		const internalQuery = qs.parse(window.location.query, { ignoreQueryPrefix: true });
		Object.assign(internalQuery, query);

		const newQuery = {
			redirect:
				window.location.href.replace(window.location.origin, '') +
				qs.stringify(internalQuery, { addQueryPrefix: true }),
		};

		return '/register?' + qs.stringify(newQuery);
	};


	async logIn({ email, password, rememberMe }) {
		// Run the logIn mutation
		const response = await this.client.mutate({
			mutation: logIn,
			variables: { email, password },
		});

		if (!response) {
			throw new Error(
				'Code level error receiving response from sign in request, please contact support.'
			);
		} else if (!response.data || !response.data.authenticateUser) {
			throw new Error(
				'Invalid response while trying to sign in. Are you connected to the internet?'
			);
		}

		// Save our token
		if (rememberMe) {
			window.sessionStorage.removeItem('authToken');
			window.localStorage.setItem('authToken', response.data.authenticateUser.token);
      window.localStorage.setItem('userId', response.data.authenticateUser.id);
		} else {
			window.localStorage.removeItem('authToken');
			window.localStorage.removeItem('userId');
			window.sessionStorage.setItem('authToken', response.data.authenticateUser.token);
      window.sessionStorage.setItem('userId', response.data.authenticateUser.id);
		}

		try {
			// Tell Apollo anything it has cached is invalid and it needs to flush its cache.
			await this.client.resetStore();

		} catch (e) {
			// ignore error
		}

		// Save questionnaire results if needed
		await this.saveQuestionnaireResults();

		// Let everyone know we changed the logged in user.
		this.notifySubscribersOfChange();

		return true;
	}


	async createUserAndLogIn({ type, email, password, firstName, lastName }) {
		// Run the createUser and log in nested mutation
		const response = await this.client.mutate({
			mutation: createUserAndLogIn,
			variables: {
				type,
				email,
				password,
				firstName,
				lastName,
			},
		});

		if (!response) {
			throw 'Code level error receiving response from create user request, please contact support.';
		} else if (!response.data || !response.data.registerUser) {
			throw 'Invalid response while trying to create user. Are you connected to the internet?';
		}

		// Store our token and tell Apollo anything it has cached is invalid
		window.sessionStorage.removeItem('authToken');
		window.sessionStorage.removeItem('userId');
		window.localStorage.setItem('authToken', response.data.registerUser.token);
		window.localStorage.setItem('userId', response.data.registerUser.id);
		this.client.resetStore();

		// Save questionnaire results if needed
		await this.saveQuestionnaireResults();

		// Let everyone know we changed the logged in user.
		this.notifySubscribersOfChange();

		return response.data.registerUser.id;
	}


	async resetPassword({ email }) {
		// run the resetPassword mutation
		const response = await this.client.mutate({
			mutation: resetPassword,
			variables: {
				email
			}
		});

		if (!response) {
			throw new Error('Code level error receiving response from password reset request, please contact support.');
		} else if (!response.data || !response.data.resetPassword.email) {
			throw new Error('Invalid response while trying to reset password. Is email valid?');
		}

		return true;
	}


	async setNewPassword({ email, token, password }) {
		// Run the setNewPassword mutation
		const response = await this.client.mutate({
			mutation: setNewPassword,
			variables: {
				email,
				password,
				token,
			},
		});

		if (!response) {
			throw new Error('Code level error receiving response from set new password request, please contact support.');
		} else if (!response.data || !response.data.setNewPassword.email) {
			throw new Error('Invalid response while trying to set new password.');
		}

		// Run the logIn mutation
		const loginResponse = await this.client.mutate({
			mutation: logIn,
			variables: { email, password },
		});

		if (!loginResponse) {
			throw new Error('Code level error receiving response from sign in request, please contact support.');
		} else if (!loginResponse.data || !loginResponse.data.authenticateUser) {
			throw new Error('Invalid response while trying to sign in. Are you connected to the internet?');
		}

		// Store our token and tell Apollo anything it has cached is invalid
		window.sessionStorage.removeItem('authToken');
		window.sessionStorage.removeItem('userId');
		window.localStorage.setItem('authToken', loginResponse.data.authenticateUser.token);
		window.localStorage.setItem('userId', loginResponse.data.authenticateUser.id);
		this.client.resetStore();

		// Let everyone know we changed the logged in user.
		this.notifySubscribersOfChange();

		return true;
	}

  async getQuestionnaireToken() {
    // generate a random token from hasura to identify the sessions for non-logged-in users
    if (!window.sessionStorage.getItem('token')) {
      const { data: { createToken: { token } = {} } = {} } = await this.client.query({
        query: createToken
      });
      window.sessionStorage.setItem('token', token);
    }

    return window.sessionStorage.getItem('token');
  }

	logout = () => {
		// Clear unsaved screener and assessment results
		window.sessionStorage.removeItem('screener');
		window.sessionStorage.removeItem('assessment');

		// Clear our token and tell Apollo anything it has cached is invalid
		window.localStorage.removeItem('authToken');
		window.localStorage.removeItem('userId');
		window.sessionStorage.removeItem('authToken');
		window.sessionStorage.removeItem('userId');
		window.sessionStorage.removeItem('token');
		this.client.resetStore();

		// Let everyone know we changed the logged in user.
		this.notifySubscribersOfChange();

		// And redirect to the login page if they're on an authenticated route.
		this.forceLoginIfApplicable();

		return true;
	};


	get isLoggedIn() {
		return !!this.authToken;
	}


	get authToken() {
		if (typeof window === 'undefined') return null;

    const aTok = window.sessionStorage.getItem('authToken') || window.localStorage.getItem('authToken');

    if (!aTok) return null;

    // TODO: now parse the token and check if it's over a minute until expiry.
    // common/index.ts in the aws functions for the frontsite defines the JWT format.
    const decodedToken = jwt_decode(aTok);
    // test

    // for now, let's just return the token, so the site still works locally
    return aTok
	}

  get userId() {
    if (typeof window === 'undefined') return null;

    return window.sessionStorage.getItem('userId') || window.localStorage.getItem('userId');
  }


	notifySubscribersOfChange = () => {
		for (const subscription of this.subscriptions) {
			subscription();
		}
	};

	async saveQuestionnaireResults() {
		if (typeof window === 'undefined') return;

    // link any questionnaire/assessment to the current logged in user.
    const token = await this.getQuestionnaireToken();
    if (token) {
      await this.client.mutate({
        mutation: linkToken,
        variables: { token }
      });
    }
	}
}



// WEBPACK FOOTER //
// ./src/managers/authManager/authManager.js